import React, { useCallback, useState } from 'react';
import { ClientsUserOverview } from '../components/users/ClientsUserOverview';
import { ClientUsersTab } from '../components/clients/ClientUsersTab';
import { CreateUserModal } from '../components/users/CreateUserModal';
import { User } from '../types/user';
import { Client } from '../types/client';
import { useI18n } from '../contexts/I18nContext';
import { useClientAccess } from '../hooks/useClientAccess';
import '../styles/users.css';

/**
 * User management, organised by client.
 *
 * A technician belongs to exactly one client and a ClientAdmin only ever
 * manages their own, so the organisation is the natural first axis: pick a
 * client, then work inside it. The previous flat list mixed every client's
 * users into one table where the client was just another column.
 */
export const UsersPage: React.FC = () => {
  const { t } = useI18n();
  const { isSuperAdmin, isAdmin, canWriteUsers } = useClientAccess();
  // ANEP staff choose an organisation; a customer's admin has only one.
  const picksAmongClients = isSuperAdmin || isAdmin;
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleUserCreated = useCallback((_user: User) => {
    setRefreshKey(prev => prev + 1);
  }, []);

  return (
    <div className="page users-page">
      <div className="page-header">
        <div className="page-title">
          {selectedClient ? (
            <>
              {/* No breadcrumb when there was no choice to go back to. */}
              {picksAmongClients && (
                <nav className="users-breadcrumb" aria-label={t('users.title')}>
                  <button
                    type="button"
                    className="breadcrumb-link"
                    data-testid="btn-back-to-clients"
                    onClick={() => setSelectedClient(null)}
                  >
                    {t('users.allClients')}
                  </button>
                  <span className="breadcrumb-sep" aria-hidden="true">/</span>
                  <span className="breadcrumb-current">{selectedClient.name}</span>
                </nav>
              )}
              <h1>{selectedClient.name}</h1>
              <p>{t('users.clientUsersSubtitle')}</p>
            </>
          ) : (
            <>
              <h1>{t('users.title')}</h1>
              <p>{t('users.subtitle')}</p>
            </>
          )}
        </div>

        <div className="page-actions">
          <button
            className="refresh-button"
            onClick={() => setRefreshKey(prev => prev + 1)}
          >
            {t('common.refresh')}
          </button>
          {/* Creating a user only makes sense inside a client: that is what
              decides the groups and the access the account ends up with. */}
          {selectedClient && canWriteUsers && (
            <button
              className="create-user-btn"
              data-testid="btn-add-user"
              onClick={() => setShowCreateModal(true)}
            >
              <span className="add-type-icon" aria-hidden="true">+</span>
              {t('users.createNew')}
            </button>
          )}
        </div>
      </div>

      <div className="page-content users-content">
        {selectedClient ? (
          <ClientUsersTab
            key={`${selectedClient.id}-${refreshKey}`}
            clientId={selectedClient.id}
            clientName={selectedClient.name}
            clientAccess={selectedClient.provisioning_access ?? true}
          />
        ) : (
          <ClientsUserOverview
            onClientSelect={setSelectedClient}
            refreshTrigger={refreshKey}
            autoSelectSingle={!picksAmongClients}
          />
        )}
      </div>

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUserCreated={handleUserCreated}
        defaultClientId={selectedClient?.id}
      />
    </div>
  );
};
